import { WorkflowExecutionDto } from '@zorro/clients';
import { useEffect, useState } from 'react';

import { useMonolithQuery } from './useMonolithQuery';

const DEFAULT_POLL_INTERVAL_MS = 2000;

type WorkflowProgressOptions = {
  onComplete?: (workflow: WorkflowExecutionDto) => void;
  onError?: (workflow: WorkflowExecutionDto) => void;
};

export function useWorkflowProgress(
  correlationId: string | null,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  options: WorkflowProgressOptions = {}
) {
  const [workflow, setWorkflow] = useState<WorkflowExecutionDto | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const { data, refetch } = useMonolithQuery({
    method: 'workflowsControllerGetByCorrelationId',
    params: correlationId ? [correlationId] : undefined,
    enabled: !!correlationId,
  });

  // Keep workflow in sync with query data
  useEffect(() => {
    if (!correlationId) {
      setWorkflow(null);
      return;
    }
    setWorkflow(data ?? null);
  }, [correlationId, data]);

  // Poll via refetch until terminal
  useEffect(() => {
    if (!correlationId) {
      setIsPolling(false);
      return () => {};
    }

    let intervalId: ReturnType<typeof setInterval> | undefined;
    let stopped = false;

    const poll = async () => {
      if (stopped) return;
      setIsPolling(true);
      try {
        const result = await refetch();
        if (stopped) return;

        if (result.isError) {
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = undefined;
          }
          setIsPolling(false);
          setError(result.error);
          return;
        }

        const latest = (result.data as WorkflowExecutionDto | null) ?? null;
        setWorkflow(latest);

        const isTerminal =
          latest?.status === 'COMPLETED' || latest?.status === 'FAILED';

        if (isTerminal && intervalId) {
          clearInterval(intervalId);
          intervalId = undefined;
          setIsPolling(false);
        }
      } catch (err) {
        if (stopped) return;
        setError(err);
      }
    };

    // initial fetch
    void poll();
    intervalId = setInterval(poll, pollIntervalMs);

    return () => {
      stopped = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
      setIsPolling(false);
    };
  }, [correlationId, pollIntervalMs, refetch]);

  const progress =
    workflow && workflow.totalItems > 0
      ? workflow.processedItems / workflow.totalItems
      : 0;

  // Destructure callbacks for stable references in dependency array
  const { onComplete, onError } = options;

  // Fire callbacks when reaching terminal states
  useEffect(() => {
    if (!workflow) return;
    if (workflow.status === 'COMPLETED') {
      onComplete?.(workflow);
    } else if (workflow.status === 'FAILED') {
      onError?.(workflow);
    }
  }, [workflow, onComplete, onError]);

  return {
    workflow,
    isPolling,
    error,
    progress,
    status: workflow?.status,
  };
}
